import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mountain, Pause, Play, Trash2, RefreshCw, ArrowLeft, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const formatDate = (iso) => iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

export default function DashboardRadarMineral() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [novoProcesso, setNovoProcesso] = useState("");
  const [criando, setCriando] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/intel/watches");
      const mineral = (r.data.items || []).filter(w => w.kind === "mineral");
      setItems(mineral);
    } catch { toast.error("Falha ao carregar radares"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const criarWatch = async (e) => {
    e.preventDefault();
    if (!novoProcesso.trim()) { toast.error("Informe o número do processo"); return; }
    setCriando(true);
    try {
      await api.post("/intel/watches", { kind: "mineral", processo: novoProcesso.trim() });
      toast.success("Processo mineral em monitoramento");
      setNovoProcesso("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message ||
                    err?.response?.data?.detail ||
                    "Falha ao criar monitoramento");
    } finally { setCriando(false); }
  };

  const togglePause = async (w) => {
    try {
      if (w.active) {
        await api.delete(`/intel/watches/${w.watch_id}`);
        toast.success("Monitoramento pausado");
      } else {
        await api.post(`/intel/watches/${w.watch_id}/reactivate`);
        toast.success("Monitoramento reativado");
      }
      await load();
    } catch { toast.error("Falha ao alternar status"); }
  };

  const checkNow = async (w) => {
    try {
      await api.post(`/intel/watches/${w.watch_id}/check-now`);
      toast.success("Verificação disparada");
      setTimeout(load, 1500);
    } catch { toast.error("Falha ao verificar"); }
  };

  const remove = async (w) => {
    if (!window.confirm(`Remover monitoramento do processo ${w.processo}?`)) return;
    try {
      await api.delete(`/intel/watches/${w.watch_id}`);
      toast.success("Removido");
      await load();
    } catch { toast.error("Falha ao remover"); }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="dashboard-radar-mineral">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 mb-3">
        <ArrowLeft className="w-3.5 h-3.5"/> Voltar ao dashboard
      </Link>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl inline-flex items-center justify-center"
             style={{ background: "linear-gradient(135deg, #0B1B2F, #071B34)" }}>
          <Mountain className="w-5 h-5" style={{ color: "#F0D37A" }}/>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Hermes Radar</div>
          <h1 className="font-heading text-3xl font-semibold text-slate-900">Processos minerários monitorados</h1>
          <p className="text-sm text-slate-500 mt-1">
            O Hermes alerta por e-mail quando mudar a fase, o titular, a substância ou o último evento.
          </p>
        </div>
      </div>

      {/* Novo */}
      <form onSubmit={criarWatch} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-6 flex flex-wrap gap-3 items-end"
            data-testid="radar-mineral-form">
        <div className="flex-1 min-w-[260px]">
          <label className="block text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
            Novo processo para monitorar
          </label>
          <input value={novoProcesso} onChange={(e) => setNovoProcesso(e.target.value)}
            placeholder="880954/1985"
            data-testid="input-novo-processo-mineral"
            className="w-full h-10 px-3 rounded-lg border border-slate-200 mt-1 text-sm font-mono"/>
        </div>
        <button type="submit" disabled={criando}
          data-testid="btn-monitorar-processo-mineral"
          className="h-10 px-4 rounded-lg font-semibold text-sm inline-flex items-center gap-1.5 shadow-sm transition disabled:opacity-60"
          style={{ background: "linear-gradient(180deg,#F5DE8C,#D4AF37)", color: "#0B1B2F" }}>
          <Plus className="w-3.5 h-3.5"/> Monitorar
        </button>
      </form>

      {/* Lista */}
      {loading ? (
        <div className="p-12 text-center text-slate-400 text-sm">Carregando…</div>
      ) : items.length === 0 ? (
        <div className="p-12 text-center text-slate-400 text-sm bg-white rounded-2xl border border-slate-200" data-testid="radar-mineral-empty">
          Você ainda não monitora nenhum processo minerário.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(w => {
            const key = w.last_snapshot_key || {};
            return (
              <div key={w.watch_id} className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition"
                   data-testid={`radar-mineral-item-${w.watch_id}`}>
                <div className="flex flex-wrap items-start gap-4 justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Mountain className="w-4 h-4 text-slate-400"/>
                      <span className="font-heading text-lg font-semibold text-slate-900 truncate">
                        {w.processo || w.processo_input}
                      </span>
                      {!w.active && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">PAUSADO</span>
                      )}
                      {w.active && w.total_alerts > 0 && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                          {w.total_alerts} alerta{w.total_alerts === 1 ? "" : "s"}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mt-3">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Fase</div>
                        <div className="text-slate-900 font-medium">{key.fase || "—"}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Substância</div>
                        <div className="text-slate-900 font-medium">{key.substancia || "—"}</div>
                      </div>
                      <div className="md:col-span-2">
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Titular</div>
                        <div className="text-slate-900 font-medium truncate">{key.titular || "—"}</div>
                      </div>
                      <div className="md:col-span-2">
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Último evento</div>
                        <div className="text-slate-800 text-xs">{key.ultimo_evento || "—"}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Última checagem</div>
                        <div className="text-slate-800 text-xs">{formatDate(w.last_check_at)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Último alerta</div>
                        <div className="text-slate-800 text-xs">{formatDate(w.last_alert_at)}</div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <button onClick={() => checkNow(w)} data-testid={`btn-check-now-${w.watch_id}`}
                      className="h-8 px-3 rounded-lg text-xs font-medium bg-slate-100 hover:bg-slate-200 inline-flex items-center gap-1.5 transition">
                      <RefreshCw className="w-3 h-3"/> Verificar agora
                    </button>
                    <button onClick={() => togglePause(w)} data-testid={`btn-toggle-${w.watch_id}`}
                      className="h-8 px-3 rounded-lg text-xs font-medium bg-slate-100 hover:bg-slate-200 inline-flex items-center gap-1.5 transition">
                      {w.active ? <><Pause className="w-3 h-3"/> Pausar</> : <><Play className="w-3 h-3"/> Reativar</>}
                    </button>
                    <button onClick={() => remove(w)} data-testid={`btn-remove-${w.watch_id}`}
                      className="h-8 px-3 rounded-lg text-xs font-medium text-rose-600 hover:bg-rose-50 inline-flex items-center gap-1.5 transition">
                      <Trash2 className="w-3 h-3"/> Remover
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
